import { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import { svgPoint } from "./useEditorViewport";
import { BoxSelect, Rows3, CheckSquare, XSquare } from "lucide-react";

const STEP_M = 0.05;

// Selezione multipla pannelli (rettangolo, Ctrl/Shift o modalità multipla, fila, tutti) + spostamento a blocchi.
export function usePanelSelection({ panels, setPanels, clientToM, geo, editable = true, mode }) {
  const [sel, setSel] = useState(() => new Set());
  const [multi, setMulti] = useState(false);
  const [box, setBox] = useState(null);
  const drag = useRef(null);
  const marquee = useRef(null);

  const toPx = (e) => svgPoint(e.currentTarget.ownerSVGElement || e.currentTarget, e) || { x: 0, y: 0 };
  const additive = (e) => multi || e.shiftKey || e.ctrlKey || e.metaKey;
  const shift = (ids, dx, dy) => setPanels((ps) => ps.map((p) => (ids.has(p.id) ? { ...p, cx: p.cx + dx, cy: p.cy + dy } : p)));

  const onPanelDown = (e, p) => {
    if (!editable) return;
    e.stopPropagation();
    const add = additive(e);
    // in modalità multipla: clic su non selezionato lo aggiunge; clic (senza trascinare) su selezionato lo toglie
    const group = add ? new Set([...sel, p.id]) : sel.has(p.id) ? new Set(sel) : new Set([p.id]);
    setSel(group);
    if (mode !== "move") { if (add && sel.has(p.id)) setSel((s) => { const n = new Set(s); n.delete(p.id); return n; }); return; }
    const m = clientToM(e); if (!m) return;
    drag.current = { ids: group, grab: p.id, start: m, moved: false, toggleOff: add && sel.has(p.id), orig: new Map(panels.filter((q) => group.has(q.id)).map((q) => [q.id, { cx: q.cx, cy: q.cy }])) };
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* noop */ }
  };
  // true se ha gestito l'evento (modalità sposta: avvia rettangolo di selezione)
  const onBgDown = (e) => {
    if (!editable || mode !== "move") { if (!additive(e)) setSel(new Set()); return mode === "move"; }
    const p = toPx(e);
    marquee.current = { x0: p.x, y0: p.y, add: additive(e) };
    if (!additive(e)) setSel(new Set());
    setBox({ x: p.x, y: p.y, w: 0, h: 0 });
    return true;
  };
  const onMove = (e) => {
    const d = drag.current;
    if (d) {
      const m = clientToM(e); if (!m || !isFinite(m.x) || !isFinite(m.y)) return;
      const dx = m.x - d.start.x, dy = m.y - d.start.y;
      if (!d.moved && Math.hypot(dx, dy) < 0.02) return;
      d.moved = true;
      setPanels((ps) => ps.map((q) => { const o = d.orig.get(q.id); return o ? { ...q, cx: o.cx + dx, cy: o.cy + dy } : q; }));
      return;
    }
    const mq = marquee.current;
    if (mq) {
      const { x, y } = toPx(e);
      setBox({ x: Math.min(mq.x0, x), y: Math.min(mq.y0, y), w: Math.abs(x - mq.x0), h: Math.abs(y - mq.y0) });
    }
  };
  // snapGrab(p) → centro agganciato del pannello afferrato; lo stesso delta si applica a tutto il blocco
  const onUp = (snapGrab) => {
    const d = drag.current;
    if (d) {
      drag.current = null;
      if (!d.moved) { if (d.toggleOff) setSel((s) => { const n = new Set(s); n.delete(d.grab); return n; }); return; }
      if (snapGrab) {
        const g = panels.find((q) => q.id === d.grab);
        const s = g && snapGrab(g, d.ids);
        if (s) shift(d.ids, s.x - g.cx, s.y - g.cy);
      }
      return;
    }
    const mq = marquee.current;
    if (mq) {
      marquee.current = null;
      const b = box; setBox(null);
      if (!b || (b.w < 4 && b.h < 4)) return;
      const hit = panels.filter((p) => { const q = geo.px({ x: p.cx, y: p.cy }); return q.x >= b.x && q.x <= b.x + b.w && q.y >= b.y && q.y <= b.y + b.h; }).map((p) => p.id);
      setSel((s) => new Set([...(mq.add ? s : []), ...hit]));
    }
  };

  const selectAll = () => setSel(new Set(panels.map((p) => p.id)));
  const clear = () => setSel(new Set());
  const selectRow = () => {
    const ref = panels.find((p) => sel.has(p.id)); if (!ref) return;
    const a = (ref.ang * Math.PI) / 180, nx = -Math.sin(a), ny = Math.cos(a);
    const same = (p) => Math.abs((((p.ang - ref.ang) % 180) + 180) % 180) < 5 || Math.abs((((p.ang - ref.ang) % 180) + 180) % 180) > 175;
    const row = panels.filter((p) => same(p) && Math.abs((p.cx - ref.cx) * nx + (p.cy - ref.cy) * ny) < (ref.h || 1) / 2).map((p) => p.id);
    setSel((s) => new Set([...s, ...row]));
  };
  const remove = () => { if (!sel.size) return; setPanels((ps) => ps.filter((p) => !sel.has(p.id))); setSel(new Set()); };

  useEffect(() => {
    if (!editable) return undefined;
    const onKey = (e) => {
      const t = e.target; if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      if (e.key === "Escape") { setSel(new Set()); return; }
      if (!sel.size) return;
      const v = { ArrowUp: [0, STEP_M], ArrowDown: [0, -STEP_M], ArrowLeft: [-STEP_M, 0], ArrowRight: [STEP_M, 0] }[e.key];
      if (v) { e.preventDefault(); shift(sel, v[0], v[1]); return; }
      if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); remove(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return { sel, setSel, multi, setMulti, box, onPanelDown, onBgDown, onMove, onUp, selectAll, selectRow, clear, remove, isDragging: () => !!drag.current };
}

export function SelectionToolbar({ s, prefix }) {
  return (
    <>
      <Button size="sm" variant={s.multi ? "default" : "outline"} onClick={() => s.setMulti((v) => !v)} data-testid={`${prefix}-multi-toggle`}><BoxSelect className="h-4 w-4 mr-1" strokeWidth={1.5} />Selezione multipla</Button>
      <Button size="sm" variant="outline" onClick={s.selectRow} disabled={!s.sel.size} data-testid={`${prefix}-select-row`}><Rows3 className="h-4 w-4 mr-1" strokeWidth={1.5} />Seleziona fila</Button>
      <Button size="sm" variant="outline" onClick={s.selectAll} data-testid={`${prefix}-select-all`}><CheckSquare className="h-4 w-4 mr-1" strokeWidth={1.5} />Seleziona tutti</Button>
      <Button size="sm" variant="outline" onClick={s.clear} disabled={!s.sel.size} data-testid={`${prefix}-deselect`}><XSquare className="h-4 w-4 mr-1" strokeWidth={1.5} />Deseleziona</Button>
      <span className="text-xs text-muted-foreground" data-testid={`${prefix}-selected-count`}>{s.sel.size} selezionati</span>
    </>
  );
}

export function MarqueeRect({ box }) {
  if (!box) return null;
  return <rect x={box.x} y={box.y} width={box.w} height={box.h} fill="#FDD00322" stroke="#FDD003" strokeWidth="1.5" strokeDasharray="5 3" pointerEvents="none" data-testid="selection-marquee" />;
}
